import React, { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from './cn';
import { t } from '../../lib/i18n';
import { useIsNarrow } from '../../lib/useIsNarrow';

/**
 * shadcn/ui görünümünde pencere (modal).
 *
 * Üst üste açılabiliyor: Escape yalnızca en üstteki pencereyi kapatıyor, alttaki
 * açık kalıyor (profil penceresinin içinden şifre penceresi açılıyor).
 *
 * Telefonda (dar ekran) ortada duran kutu değil, alttan açılan sayfa: başparmağın
 * eriştiği yerde, ekran genişliğinde, üstünde tutamakla.
 */

/** Açık pencerelerin sırası — en sondaki en üstte. */
const openDialogs: object[] = [];

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Genişlik sınıfı — varsayılan orta boy. */
  className?: string;
}

export const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  className,
}) => {
  const identity = useRef({});
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useRef(`dialog-title-${Math.random().toString(36).slice(2, 8)}`).current;
  // Çağıranlar onClose'u çoğunlukla satır içi verir (her render'da yeni fonksiyon).
  // Efekt ona bağlı olsaydı her tuşta yeniden çalışıp odağı panele çekerdi —
  // pencere içindeki kutuya yazılamazdı.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const isSheet = useIsNarrow();

  useEffect(() => {
    if (!open) return;
    const id = identity.current;
    openDialogs.push(id);
    panelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      // Yalnızca en üstteki pencere Escape'e yanıt verir.
      if (event.key === 'Escape' && openDialogs[openDialogs.length - 1] === id) {
        event.preventDefault();
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const index = openDialogs.indexOf(id);
      if (index >= 0) openDialogs.splice(index, 1);
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex justify-center bg-black/60',
        isSheet ? 'items-end' : 'items-center p-4'
      )}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'w-full overflow-hidden border border-border bg-popover text-popover-foreground shadow-2xl outline-none',
          // Telefonda genişlik sınıfı yok sayılıyor: sayfa her zaman ekran genişliğinde.
          isSheet
            ? 'max-w-none rounded-t-[26px] border-x-0 border-b-0 animate-sheet-up'
            : cn('max-w-lg rounded-2xl', className)
        )}
      >
        {isSheet && <div aria-hidden="true" className="mx-auto mt-2.5 h-[5px] w-10 rounded-full bg-border" />}
        <div className={cn('flex items-start justify-between gap-4 px-6 pb-2', isSheet ? 'pt-3' : 'pt-6')}>
          <div className="space-y-1">
            <h2 id={titleId} className="text-xl font-bold tracking-tight">
              {title}
            </h2>
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('Kapat', 'Close')}
            className="-mr-2 -mt-2 rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className={cn('overflow-y-auto px-6 py-4', isSheet ? 'max-h-[72dvh]' : 'max-h-[70vh]')}>{children}</div>

        {footer && (
          <div className={cn('flex items-center gap-2 px-6 pb-6 pt-2', isSheet ? 'justify-stretch [&>*]:flex-1' : 'justify-end')}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
